import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PopBaseComponent } from '../../base/pop-base-component';
import type { ContributorsDetractorsChart } from './contributors-detractors-chart.model';
import { Constants } from './contributors-detractors-chart.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import { isEmptyArray, isNullish } from '../../common/utils/nullish';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';
import { Options } from 'highcharts';
import { DISPLAY_TYPE } from '../contributors-detractors/contributors-detractors.constants';
import { formatDecimalAsPercentage } from '../../common/utils/formatNumber';

/**
 * @tag pop-contributors-detractors-chart-component
 * @property {ContributorsDetractorsChart} [componentObject] Contributors and Detractors chart content
 * @attribute {ContributorsDetractorsChart} [data-component-object] Contributors and Detractors chart content
 */
@customElement(Constants.TAG_NAME)
export class PopContributorsDetractorsChartComponent extends PopBaseComponent {
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: ContributorsDetractorsChart;

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentName = PopContributorsDetractorsChartComponent.tagName;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-bar-chart');
    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  // Gets a list of the names of the items in the componentObject to be used as the labels for the x-axis in the chart
  // Categories are used to label each tick mark on the axis.
  // If no categories were to be passed, highcharts would automatically label each tick mark with a number starting from 0
  getCategories() {
    return this.componentObject?.itemList?.map((item) => item.name) || [];
  }

  // Gets the data for the series in the chart
  // To display a single color for all the bars, the data is formatted as a single series with each item as a data point in an array.
  // Each data point includes additional information for tooltips.
  getSeriesData() {
    const seriesName =
      this.componentObject?.itemType === DISPLAY_TYPE.CONTRIBUTORS
        ? Constants.TOP_TEN_CONTRIBUTORS_LABEL
        : Constants.TOP_TEN_DETRACTORS_LABEL;

    const tooltipHeader =
      this.componentObject?.itemType === DISPLAY_TYPE.CONTRIBUTORS
        ? Constants.CONTRIBUTOR_TOOLTIP_TEXT
        : Constants.DETRACTOR_TOOLTIP_TEXT;

    const chartSeries =
      this.componentObject?.itemList?.map((item) => ({
        y: item.percentTotalNetAssets,
        name: item.name,
        sector: item.sectorName || Constants.NO_DATA_VALUE,
        tooltipHeader: tooltipHeader,
      })) || [];

    return [
      {
        name: seriesName,
        data: chartSeries,
      },
    ];
  }

  getOptions(): Options {
    return {
      exporting: {
        enabled: false,
      },
      tooltip: {
        useHTML: true,
        pointFormatter() {
          return `
              ${BeaconChartBase.multiLevelTooltipPointHTML(this.name, `${formatDecimalAsPercentage(this.y, 2)}`, true)}
              ${BeaconChartBase.multiLevelTooltipPointHTML(this.sector, '', true)}
            `;
        },
      },
      xAxis: {
        categories: this.getCategories(),
      },
      yAxis: {
        max: this.componentObject?.maxPointValue,
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 445,
            },
            // Make the labels less space demanding on mobile
            chartOptions: {
              xAxis: {
                labels: {
                  step: 1,
                },
              },
            },
          },
        ],
      },
    };
  }

  render() {
    const noData =
      isEmptyArray(this.componentObject?.itemList) ||
      isNullish(this.componentObject?.itemList);

    if (noData) {
      return html``;
    }

    return this.renderComponent(
      html` <section class="contributors-detractors-chart-content">
        <beacon-bar-chart
          variant="vertical"
          tooltip-variant="multi-level"
          series="${JSON.stringify(this.getSeriesData())}"
          options="${JSON.stringify(this.getOptions())}"
          y-axis-label="${Constants.Y_AXIS_LABEL}"
          x-axis-label="${this.componentObject?.itemType ===
          DISPLAY_TYPE.CONTRIBUTORS
            ? Constants.CONTRIBUTORS_X_AXIS_LABEL
            : Constants.DETRACTORS_X_AXIS_LABEL}"
          disable-legend="true"
        >
        </beacon-bar-chart>
      </section>`,
    );
  }
}
